import React from 'react';
import {
  GridColumnMenuSort,
  GridColumnMenuFilter,
  GridColumnMenuItem,
  GridColumnMenuFilterUI
} from '@progress/kendo-react-grid';
import { Menu, MenuItem } from '@blueprintjs/core';

import { withTranslation } from 'react-i18next';
import Checkbox from '@hobs/components/Checkbox';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import { GRID_OPERATORS } from '@hobs/utils/constants';
import Styles from './ColumnMenu.module.scss';

/**
 * ð  PLEASE README BEFORE TRYING TO CHANGE SOMETHING.
 * Magic will begin below ð©
 * Custom filter usage:
 * It's not possible to set default filter values here, you have to pass it to
 * kendo grid via formatted oData filter format.
 * 1. columnMenu must be set true work
 * 2.
   customFilter: {
     data: [
       {
         name: <String> - display name of filter,
         value: <String> - value will be set in oData filtering,
         default: <Boolean> | Required,
         field: <String> | Not required, overrides field of column in oData filtering
       }
     ]
   }
 */

class ColumnMenu extends React.PureComponent {
  state = {
    customFilterValues: {}
  };

  saveSettings = () => {
    this.props.onCloseMenu();
    this.props.onSave();
  };

  componentDidMount() {
    this.setFilterValues();
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter !== prevProps.filter) {
      this.setFilterValues();
    }
  }

  getCurrentFilters = (results = [], filter, fields) => {
    if (!filter) {
      return results;
    }

    if (Array.isArray(filter)) {
      return filter.reduce((res, filtr) => {
        return this.getCurrentFilters(res, filtr, fields);
      }, results);
    }

    if (fields && fields.indexOf(filter.field) > -1) {
      results.push(filter);
    }

    return this.getCurrentFilters(results, filter.filters, fields);
  };

  fixFilterValue(value) {
    if (typeof value === 'string' && value.indexOf('<guid>') > -1) {
      return value.replace('<guid>', '').replace('</guid>', '');
    }
    return value;
  }

  // Set field filled/prefilled column menu filters on menu open
  setFilterValues() {
    if (this.props.filter) {
      const data = this.props.customFilter ? this.props.customFilter.data : [];

      const fields = data.reduce((results, filter) => {
        if (filter.field) {
          results.push(filter.field);
        }

        return results;
      }, []);

      if (!fields.length) {
        fields.push(this.props.column.field);
      }

      const activeFilters = this.getCurrentFilters(
        [],
        this.props.filter.filters,
        fields
      );

      const currentFilters = activeFilters.reduce((results, filter) => {
        const filters =
          this.props.customFilter &&
          this.props.customFilter.data.find(item => {
            return (
              item.value === filter.value ||
              item.value === `<guid>${filter.value}</guid>`
            );
          });

        if (filters) {
          const field = filter.field ? filter.field : this.props.column.field;
          const value = this.fixFilterValue(filter.value);

          results[`${field}_${value}`] = {
            ...filter,
            checked: true,
            value: filter.value
          };
        }

        return results;
      }, {});

      this.setState({
        customFilterValues: currentFilters
      });
    }
  }

  resetSettings = () => {
    this.props.onCloseMenu();
    this.props.onReset();
  };

  onFilterCheckboxSelect(item, checked) {
    let customFilterValues = null;

    const field = item.field ? item.field : this.props.column.field;

    const notCheckedCount = Object.values(this.state.customFilterValues).filter(
      item => !item.checked && !item.default
    ).length;

    if (item.default) {
      customFilterValues = Object.values(this.props.customFilter.data).reduce(
        (result, item) => {
          const value = this.fixFilterValue(item.value);

          if (!item.default) {
            const newField = item.field ? item.field : field;
            result[`${newField}_${value}`] = {
              ...item,
              checked:
                notCheckedCount !== 0 ||
                this.props.customFilter.data.length !==
                  Object.keys(this.state.customFilterValues).length + 1
            };
          }
          return result;
        },
        {}
      );
    } else {
      const value = this.fixFilterValue(item.value);

      const defaultKey = Object.values(this.state.customFilterValues).findIndex(
        field => field.default
      );

      customFilterValues = {
        ...this.state.customFilterValues,
        [`${field}_${value}`]: {
          ...item,
          checked: !checked
        }
      };

      if (defaultKey > -1) {
        customFilterValues[defaultKey] = {
          ...this.state.customFilterValues[defaultKey],
          checked: false
        };
      }
    }

    this.setState({
      customFilterValues
    });
  }

  createCustomFilter() {
    var cFilter = this.props.customFilter.filter;
    const operator = cFilter && cFilter === 'contains' ? 'contains' : 'eq';

    const currentFilters = Object.keys(this.state.customFilterValues).reduce(
      (results, key) => {
        const item = this.state.customFilterValues[key];
        if (!item.default && item.checked) {
          results.push({
            field: item.field ? item.field : this.props.column.field,
            operator: operator,
            value: item.value
          });
        }

        return results;
      },
      []
    );

    let filters = [];

    if (currentFilters.length) {
      filters.push({
        logic: 'or',
        filters: currentFilters
      });
    }

    if (this.props.filter) {
      const fields = this.props.customFilter.data.reduce((results, filter) => {
        if (filter.field) {
          results.push(filter.field);
        }

        return results;
      }, []);

      if (!fields.length) {
        fields.push(this.props.column.field);
      }

      const restFilters = [
        ...this.props.filter.filters.filter(filter => {
          if (filter.filters) {
            return filter.filters.find(
              filtr => fields.indexOf(filtr.field) === -1
            );
          }

          return fields.indexOf(filter.field) === -1;
        })
      ];

      if (restFilters.length) {
        filters = filters.concat(restFilters);
      }
    }

    return {
      logic: 'and',
      filters
    };
  }

  onlyUniqueValues(value, index, self) {
    return self.indexOf(value) === index;
  }

  handleFilter = event => {
    const { onFilterChange, onCloseMenu } = this.props;

    onCloseMenu();

    onFilterChange(this.createCustomFilter());
    event.preventDefault();
    event.stopPropagation();
  };

  customFilter() {
    const { t, customFilter, onFilterChange } = this.props;

    if (customFilter) {
      const count = Object.values(this.state.customFilterValues).filter(
        item => item.checked && !item.default
      ).length;
      return (
        <div
          tabIndex="1"
          onKeyDown={this.handleFilter}
          className={Styles['custom-filter--content']}
        >
          <div className={Styles['custom-filter--options']}>
            {customFilter.data.map(item => {
              const field = item.field ? item.field : this.props.column.field;
              const value = this.fixFilterValue(item.value);
              const checked = this.state.customFilterValues[`${field}_${value}`]
                ? this.state.customFilterValues[`${field}_${value}`].checked
                : false;
              const selectAllChecked =
                (count === 0 && item.default) ||
                (count + 1 ===
                  customFilter.data
                    .map(item => item.value)
                    .filter(this.onlyUniqueValues).length &&
                  item.default);
              return (
                <div key={ShortId.generate()}>
                  <Checkbox
                    value={item.value}
                    checked={checked || selectAllChecked}
                    onChange={() => {
                      this.onFilterCheckboxSelect(item, checked);
                    }}
                  >
                    {item.default ? <strong>{item.name}</strong> : item.name}
                  </Checkbox>
                </div>
              );
            })}
          </div>
          <div className={Styles.label}>
            {count} {t('items selected')}
          </div>
          <div className={Styles['custom-filter--actions']}>
            <RaisedButton
              type="button"
              size="small"
              onClick={this.handleFilter}
            >
              {t('Filter')}
            </RaisedButton>
            <GhostButton
              type="button"
              size="small"
              onClick={() => {
                this.props.onCloseMenu();
                this.setState(
                  {
                    customFilterValues: {}
                  },
                  () => {
                    onFilterChange(this.createCustomFilter());
                  }
                );
              }}
            >
              {t('Reset')}
            </GhostButton>
          </div>
        </div>
      );
    }

    return <></>;
  }

  render() {
    const { t, customFilter, onSave, onReset, column, sortable } = this.props;

    const operators = GRID_OPERATORS[column.filter] || GRID_OPERATORS['text'];

    return (
      <div className={Styles.menu}>
        {sortable && <GridColumnMenuSort {...this.props} />}

        {this.props.customFilter && (
          <Menu>
            <MenuItem
              onClick={event => {
                event.preventDefault();
                return null;
              }}
              className={Styles['custom-filter']}
              shouldDismissPopover={false}
              icon={<span className="k-icon k-i-filter" />}
              text={t('Filter')}
              popoverProps={{
                usePortal: false,
                className: Styles['custom-filter--popover']
              }}
            >
              {this.customFilter()}
            </MenuItem>
          </Menu>
        )}
        {!customFilter && (
          /* https://github.com/telerik/kendo-react/issues/888 */
          <div onKeyDown={e => e.stopPropagation()}>
            <GridColumnMenuFilter
              filterable={true}
              filterUI={props => (
                <GridColumnMenuFilterUI
                  {...props}
                  firstFilterProps={{
                    ...props.firstFilterProps,
                    operators: operators
                  }}
                />
              )}
              hideSecondFilter={true}
              {...this.props}
              filterOperators={GRID_OPERATORS}
            />
          </div>
        )}
        <div className={Styles.actions}>
          {onSave && (
            <GridColumnMenuItem
              onClick={this.saveSettings}
              title={t('Save settings')}
            />
          )}
          {onReset && (
            <GridColumnMenuItem
              onClick={this.resetSettings}
              title={t('Reset settings')}
            />
          )}
        </div>
      </div>
    );
  }
}

ColumnMenu.propTypes = {
  sortable: PropTypes.bool,
  onSave: PropTypes.func,
  t: PropTypes.func,
  customFilter: PropTypes.object,
  filter: PropTypes.object,
  column: PropTypes.object,
  onReset: PropTypes.func,
  onFilterChange: PropTypes.func,
  onCloseMenu: PropTypes.func
};

ColumnMenu.defaultProps = {
  sortable: true
};

export default withTranslation()(ColumnMenu);
